import React, { type ReactElement, useEffect } from 'react';
import {
	getErrorAttributes,
	isClientNetworkError,
	isHttpClientErrorResponse,
} from '@jira/assets-app__assets-common-logging/src/isClientNetworkError.tsx';
import { GenericPermissionErrorPageAsync } from '@jira/platform__generic-permission-error-page/src/GenericPermissionErrorPageAsync.tsx';
import { NotFoundErrorPageAsync } from '@jira/platform__not-found-error-page/src/NotFoundErrorPageAsync.tsx';
import { AsyncCmdbUpsellPage } from '@jira/servicedesk__cmdb-upsell-page/src/AsyncCmdbUpsellPage.tsx';
import { CmdbArchitectureContainer } from '@jira/servicedesk__cmdb-architecture/src/CmdbArchitectureContainer.tsx';
import { InsightGlobalConfigContainer } from '@jira/servicedesk__insight-global-configuration-store/src/useInsightGlobalConfig.tsx';
import { SubProductUpdater } from '@jira/spa-apps__common/src/SubProductUpdater.tsx';
import { isCMDBEnabledInEnvironment } from '@jira/servicedesk__insight-common-utils-cmdb-fedramp/src/isCMDBEnabledInEnvironment.tsx';
import {
	useInsightVisibility,
	useWorkspaceContext,
} from '@jira/servicedesk__insight-common-services-workspace-context/src/useWorkspaceContext.tsx';
import {
	getUserJsmLicenceState,
	isAssetsUser,
} from '@jira/servicedesk__insight-common-services-workspace-context/src/fetchWorkspaceId.tsx';
import type { WorkspaceId } from '@jira/servicedesk__insight-common-types-shared-types/src/toCmdbObjectTypeId.tsx';
import { useAssetsUserPermissionLoading } from '@jira/router-resources__assets-user-permission/src/useAssetsUserPermission.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import type { TrackerCallbacks } from '@jira/platform__experience-tracking-viewing/src/view/common/types.tsx';
import { ServiceDeskAnalyticsContext } from '@jira/servicedesk__analytics/src/ServiceDeskAnalyticsContext.tsx';
// eslint-disable-next-line jira/restricted/@jira+servicedesk__common-toPackageName
import {
	APP_NAMES,
	toPackageName,
	type AppName,
} from '@jira/servicedesk__common/src/toPackageName.tsx';

import type { PageWrapper } from '@jira/servicedesk__spa-commons/src/PageStyle.tsx';
import { useAssetsTenantContext } from '@jira/assets-app__assets-tenant-context-provider/src/useAssetsTenantContext.tsx';
import { useAssetsDataManagerUserAuth } from '@jira/assets-app__data-manager-common/src/useAssetsDataManagerAccess.tsx';
import PageDI from '@atlaskit/page';
import { Redirect } from '@atlassian/react-resource-router';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { NoAccessErrorPage } from './NoAccessErrorPage.tsx';
import { useHideAssetsReportsInIsolatedCloud } from './useHideAssetsReportsInIsolatedCloud.tsx';

/**
 * Page component will be deprecated very soon
 * Any new page should avoid using PageDI/PageWrapper wrapper if possible.
 */
const APP_NAMES_WITHOUT_WRAPPER: readonly AppName[] = [
	APP_NAMES.INSIGHT_LANDING_PAGE,
	APP_NAMES.INSIGHT_GLOBAL_CONFIGURATION,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_OBJECT_CLASS_SETTINGS_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_OBJECT_CLASSES_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_OBJECT_CLASSES_SETTINGS_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_DATA_SOURCE_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_OBJECT_CLASS_VIEW_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_OBJECT_DETAIL_VIEW_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_DATA_DICTIONARY_GROUP_PAGE,
	APP_NAMES.ASSETS_APP_DATA_MANAGER_ISSUES_PAGE,
] as const;

const shouldSkipWrapper = (appName?: AppName): boolean => {
	if (!appName) {
		return false;
	}
	return APP_NAMES_WITHOUT_WRAPPER.includes(appName);
};

type LegacyProps = {
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	appName?: AppName;
};

type Props = {
	appName: AppName;
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	pageStyle?: PageWrapper;
	onExperienceFailure?: TrackerCallbacks['onExperienceFailure'];
	onExperienceAbort?: TrackerCallbacks['onExperienceAbort'];
};

export const AssetsAppBase = ({
	appName,
	children,
	pageStyle = PageDI,
	onExperienceFailure,
	onExperienceAbort,
}: Props): React.JSX.Element => (
	<>
		<SubProductUpdater subProduct="serviceDesk" />
		<ServiceDeskAnalyticsContext>
			<ContextualAnalyticsData sourceName={toPackageName(appName)} sourceType={SCREEN}>
				<AssetsAppBaseInternal
					appName={appName}
					pageStyle={pageStyle}
					onExperienceFailure={onExperienceFailure}
					onExperienceAbort={onExperienceAbort}
				>
					{children}
				</AssetsAppBaseInternal>
			</ContextualAnalyticsData>
		</ServiceDeskAnalyticsContext>
	</>
);

export const AssetsAppBaseInternal = ({
	appName,
	children,
	pageStyle: PageWrapper,
	onExperienceFailure,
	onExperienceAbort,
}: {
	appName: AppName;
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	pageStyle: PageWrapper;
	onExperienceFailure?: TrackerCallbacks['onExperienceFailure'];
	onExperienceAbort?: TrackerCallbacks['onExperienceAbort'];
}): React.JSX.Element | null => {
	const { workspaceId, error, loading } = useWorkspaceContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tenantContext = useAssetsTenantContext();
	const { isLicensedAssetsUser } = tenantContext;
	const licenceState = getUserJsmLicenceState(tenantContext);
	const hasInsightPermission = useInsightVisibility();
	const userId = useAccountId();
	const { data: orgId } = useOrgId();
	const { isAssetsDataManagerUnauthorizedUser } = useAssetsDataManagerUserAuth();
	const shouldHideAssetsReports = useHideAssetsReportsInIsolatedCloud();
	const isAssetsUserPermissionLoading = useAssetsUserPermissionLoading();

	useEffect(() => {
		if (onExperienceFailure && onExperienceAbort) {
			if (workspaceId === null && !loading && !error) {
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
				onExperienceFailure(
					'assets-app-base',
					getErrorAttributes(new Error('Workspace ID is not set')),
				);
			} else if (error && !isClientNetworkError(error)) {
				const trackerFunction = isHttpClientErrorResponse(error)
					? onExperienceAbort
					: onExperienceFailure;
				trackerFunction?.('assets-app-base', getErrorAttributes(error));
			}
		}
	}, [onExperienceFailure, onExperienceAbort, workspaceId, loading, error]);

	if (!isCMDBEnabledInEnvironment()) {
		return <NotFoundErrorPageAsync />;
	}

	if (fg('assets_access_workspace_source_of_truth')) {
		// Units path: in a Unit, workspace-id resolution is the entitlement
		// signal for a secondary site (has Jira/JSM but no local Assets host).
		// If the workspace id resolves we bypass the site-local licence checks;
		// if it cannot be resolved, Assets is not available on this site's Unit
		// so we show the upsell page instead of redirecting away.
		if (!userId) {
			return <Redirect to="/" />;
		}

		if (workspaceId == null) {
			if (loading) {
				return null;
			}
			// A fetch error (not just "no Assets here") must surface as a permission error rather
			// than the upsell, matching the gate-off path's `!loading && !error` guard below.
			return error ? <GenericPermissionErrorPageAsync /> : <AsyncCmdbUpsellPage />;
		}

		if (!hasInsightPermission && (isAssetsUserPermissionLoading || isLicensedAssetsUser)) {
			return null;
		}

		if (!hasInsightPermission || error) {
			return <GenericPermissionErrorPageAsync />;
		}

		if (isAssetsDataManagerUnauthorizedUser) {
			return <NoAccessErrorPage />;
		}

		if (shouldHideAssetsReports) {
			return <NotFoundErrorPageAsync />;
		}
	} else {
		if (!licenceState.siteHasJsmInstalled || !userId) {
			return <Redirect to="/" />;
		}

		if (!licenceState.siteIsPremium) {
			return <AsyncCmdbUpsellPage />;
		}

		if (!hasInsightPermission && (isAssetsUserPermissionLoading || isLicensedAssetsUser)) {
			return null;
		}

		if (!licenceState.userLicensed) {
			return <NoAccessErrorPage />;
		}

		if (workspaceId == null) {
			return !loading && !error ? <NotFoundErrorPageAsync /> : null;
		}

		if (!hasInsightPermission || error) {
			return <GenericPermissionErrorPageAsync />;
		}

		if (isAssetsDataManagerUnauthorizedUser) {
			return <NoAccessErrorPage />;
		}

		if (shouldHideAssetsReports) {
			return <NotFoundErrorPageAsync />;
		}
	}

	const shouldSkipPageWrapper = shouldSkipWrapper(appName) && fg('assets_common_schema_listing');

	const content = (
		<CmdbArchitectureContainer workspaceId={workspaceId}>
			{children({ workspaceId })}
		</CmdbArchitectureContainer>
	);

	return (
		<InsightGlobalConfigContainer
			isGlobal
			workspaceId={workspaceId}
			createAnalyticsEvent={createAnalyticsEvent}
			userId={userId}
			orgId={orgId}
		>
			{shouldSkipPageWrapper ? content : <PageWrapper>{content}</PageWrapper>}
		</InsightGlobalConfigContainer>
	);
};

// Clean this up while cleaning assets_as_an_app_v2 ff
export const InsightAppBaseV2 = ({ children, appName }: LegacyProps): React.JSX.Element | null => {
	const { workspaceId, error, loading } = useWorkspaceContext();
	const hasInsightPermission = useInsightVisibility();
	const tenantContext = useAssetsTenantContext();

	if (!isCMDBEnabledInEnvironment()) {
		return <NotFoundErrorPageAsync />;
	}

	if (!isAssetsUser(tenantContext)) {
		return <AsyncCmdbUpsellPage />;
	}

	if (workspaceId == null) {
		return !loading && !error ? <NotFoundErrorPageAsync /> : null;
	}

	if (hasInsightPermission === false || error) {
		return <GenericPermissionErrorPageAsync />;
	}

	const shouldSkipPageWrapper = shouldSkipWrapper(appName) && fg('assets_common_schema_listing');

	const content = (
		<CmdbArchitectureContainer workspaceId={workspaceId}>
			{children({ workspaceId })}
		</CmdbArchitectureContainer>
	);

	return shouldSkipPageWrapper ? content : <PageDI>{content}</PageDI>;
};
